/**
 * SendContactDialog — Enviar um CONTATO na conversa (card/vCard).
 *
 * Padrão copiado de `ForwardMessageDialog` (Modal shared com tema claro fixo,
 * busca server-side via `useContacts` condicionada a `isOpen`, lista com
 * avatar) — versão SINGLE-SELECT: escolher 1 contato e enviar como
 * `content: [{ type: 'contact', name, phone?, email? }]` pelo POST /send
 * existente (spec 2026-08-19, Fase 3).
 */

import React, { useEffect, useMemo, useState } from 'react';
import type { ContactIdentifiers, MessageContent } from '@troia-v3/types';
import { Button, Input } from '@/shared/components/ui';
import { Modal, ModalHeader, ModalFooter } from '@/shared/components/ui/Modal';
import { Icon } from '@/shared/icons';
import { useContacts } from '../hooks';
import { contactCardPhone } from '../utils';

interface SendContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Envia o content montado (caller usa o mesmo pipeline do POST /send). */
  onSend: (content: MessageContent[]) => void;
  isLoading?: boolean;
}

interface ContactRow {
  id: string;
  name: string;
  picture?: string;
  phone?: string;
  email?: string;
  subtitle: string;
}

const SendContactDialog: React.FC<SendContactDialogProps> = ({
  isOpen,
  onClose,
  onSend,
  isLoading = false,
}) => {
  const [selected, setSelected] = useState<ContactRow | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Reset-on-close (achado da revisão adversarial 2026-08-19): o ChatBox monta
  // este dialog incondicionalmente e o fechamento pós-envio não passa pelo
  // handleClose — sem este effect, reabrir mostrava o contato anterior já
  // selecionado com "Enviar contato" habilitado.
  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      setSearchTerm('');
    }
  }, [isOpen]);

  // Busca server-side, só com o modal aberto (lazy fetching — regra da casa)
  const { data: contactsData, isLoading: contactsLoading } = useContacts({
    page: 1,
    limit: 100,
    ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
  }, isOpen);

  const rows = useMemo<ContactRow[]>(() => {
    const contacts = contactsData?.items ?? [];
    return contacts.map((c) => {
      const ids: Partial<ContactIdentifiers> = c.identifiers ?? {};
      const phone = contactCardPhone(ids);
      const email = ids.email?.[0];
      return {
        id: c.id,
        name: c.name ?? 'Sem nome',
        ...(c.picture ? { picture: c.picture } : {}),
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
        subtitle: phone ?? email ?? '—',
      };
    });
  }, [contactsData]);

  const handleSend = () => {
    if (!selected) return;
    const card: MessageContent = {
      type: 'contact',
      name: selected.name,
      ...(selected.phone ? { phone: selected.phone } : {}),
      ...(selected.email ? { email: selected.email } : {}),
    };
    onSend([card]);
  };

  const handleClose = () => {
    setSelected(null);
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="md" closeOnEscape={!isLoading}>
      <div data-testid="send-contact-dialog">
        <ModalHeader title="Enviar contato" onClose={isLoading ? undefined : handleClose} />

        {/* Preview do card que será enviado */}
        <div className="mx-5 my-3 px-3 py-2 rounded-md" style={{ backgroundColor: '#f8fafc', borderLeft: '3px solid #8b5cf6' }}>
          <div className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: '#8b5cf6' }}>
            Será enviado como cartão de contato
          </div>
          <div className="text-xs" style={{ color: '#475569' }} data-testid="send-contact-preview">
            {selected
              ? `${selected.name}${selected.phone ? ` · ${selected.phone}` : ''}${selected.email ? ` · ${selected.email}` : ''}`
              : 'Selecione um contato abaixo'}
          </div>
        </div>

        {/* Busca */}
        <div className="px-5 mb-2">
          <Input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar contato por nome ou identificador…"
            icon="search"
            data-testid="send-contact-search-input"
          />
        </div>

        {/* Lista — scroll interno, mesma altura do Forward */}
        <div className="overflow-y-auto max-h-[46vh] sm:max-h-[320px] px-3 pb-2" data-testid="send-contact-list">
          {contactsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="loader" className="w-5 h-5 animate-spin" style={{ color: '#8b5cf6' }} />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: '#94a3b8' }}>
              Nenhum contato encontrado
            </div>
          ) : (
            rows.map((c) => {
              const isSelected = selected?.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelected(isSelected ? null : c)}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-colors"
                  style={{
                    minHeight: 44,
                    backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                    border: isSelected ? '1px solid #ddd6fe' : '1px solid transparent',
                  }}
                  data-testid={`send-contact-row-${c.id}`}
                >
                  {c.picture ? (
                    <img src={c.picture} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <span
                      className="flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                      style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)' }}
                    >
                      {c.name.substring(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate" style={{ color: '#1e293b' }} data-testid={`send-contact-row-name-${c.id}`}>{c.name}</span>
                    <span className="block text-xs truncate" style={{ color: '#94a3b8' }}>{c.subtitle}</span>
                  </span>
                  {isSelected && <Icon name="check" className="w-4 h-4 flex-shrink-0" style={{ color: '#8b5cf6' }} />}
                </button>
              );
            })
          )}
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading} data-testid="send-contact-cancel">
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!selected || isLoading} data-testid="send-contact-confirm">
            {isLoading ? 'Enviando…' : 'Enviar contato'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default SendContactDialog;
